import { css } from '@emotion/react';
import { Avatar, MenuItem, Select, Rating } from '@mui/material';
import React, { memo } from 'react';
import useMedia from '~/hooks/useMedia';
import { WHITE, globalRed, globalGreen, globalGrey } from '~/modules/AppLayout/Colors';
import { ai, flex, jc } from '~/modules/AppLayout/FlexGridCss';
import { fontWeight600 } from '~/css/font';
import { useFavicon } from 'react-use';
import { __IS_CLIENT__ } from '~/utils/__IS_CLIENT__';
/** import related modules */
// print keys
import { selectors } from '~/modules/tradeSheet/tradingSummary/selectors';
// data
import { useTradingSummaryState } from '~/modules/tradeSheet/tradingSummary/useTradingSummaryState';
import { useTradingSummaryUserAccountState } from '~/modules/tradeSheet/tradingSummary/usetradingSummaryUserAccountState';
// components
import TimeRangeTabs from '~/modules/tradeSheet/shared/TimeRangeTabs';
import AccumulatedReturnRateChart from '~/modules/tradeSheet/tradingSummary/AccumulatedReturnRateChart';
import NotFoundPage from '~/modules/tradeSheet/tradingSummary/NotFoundPage';
import PositionDuration from '~/modules/tradeSheet/tradingSummary/PositionDuration';
import ReturnRate from '~/modules/tradeSheet/tradingSummary/ReturnRate';
import ToolTipDirections from '~/modules/tradeSheet/tradingSummary/ToolTipDirections';
import TopThreeProfitAndLoss from '~/modules/tradeSheet/tradingSummary/TopThreeProfitAndLoss';
import TradingFrequency from '~/modules/tradeSheet/tradingSummary/TradingFrequency';
import TradingProporion from '~/modules/tradeSheet/tradingSummary/TradingProportion';
import TradingStrengthOverall from '~/modules/tradeSheet/tradingSummary/TradingOverall';
import { PageToDirectPrint, PageToSnapShot } from '~/modules/tradeSheet/tradingSummary/SaveMe';
// css
import { fubonHeaderCss, fubonTitleCss, fubonTimeAndActionSectionCss, printerSectionCss, avatarCss, accountBoxCss, accountInfoCss, accountNameCss, accountNumberCss, headerItemKeyCss, } from '~/modules/tradeSheet/tradingSummary/tradingSummaryCss';
// style-components
import { FubonHeader1, FubonHeader2, FubonHeader3, FubonLogo, } from '~/modules/tradeSheet/tradingSummary/tradingStyleComponent';
import { createMobileCss2 } from '~/css/createMobileCss';
const sectionTitleCss = css `
  align-self: flex-start;
  ${fontWeight600};
  font-size: 1rem;
`;
const sectionItemCss = css `
  border-radius: 8px;
  background: ${WHITE};
  padding: 16px;
  margin: 8px;
`;
export const TradingSummaryNextPage = memo(function TradingSummaryNextPage() {
    useFavicon('/trading_summary/favicon.png');
    return (<useTradingSummaryUserAccountState.Provider>
      <Overall />
    </useTradingSummaryUserAccountState.Provider>);
});
export default TradingSummaryNextPage;
const isChrome = () => {
    const browserAgent = __IS_CLIENT__ ? navigator.userAgent : '';
    return browserAgent.indexOf('Chrome') !== -1;
};
const Overall = memo(function Overall() {
    const { isPhone, isPc } = useMedia();
    const { state: authState, acts: authActs } = useTradingSummaryUserAccountState.useContainer();
    const rootCss = css `
    ${flex.v.allCenter};
    width: ${isPc ? '1100px' : '100%'};
    background-color: #eeeeee;
    margin: 0 auto;
    & p {
      margin: 0;
    }
  `;
    return (<useTradingSummaryState.Provider>
      <div css={css `
          min-height: 100vh;
          width: 100%;
          background-color: #eeeeee;
        `}>
        <div id={selectors.PRINT_ROOT}>
          <div css={fubonHeaderCss}>
            <FubonHeader1 />
            <FubonHeader2 className={selectors.PRINT_BE_SHORTER}>
              <FubonLogo src='trading_summary/favicon-fubon.png'/>
              <p css={fubonTitleCss}>客戶交易健檢報告</p>
            </FubonHeader2>
            <FubonHeader3 />
          </div>
          {authState.clientGotWrongKey ? (<NotFoundPage />) : (<div css={rootCss} id='mytest'>
              {/* start 時間區間選項 & 列印截圖 */}
              <div css={fubonTimeAndActionSectionCss}>
                <div>
                  <TimeRangeTabs />
                </div>
                <div css={printerSectionCss}>
                  <PageToSnapShot selectorId={`#${selectors.PRINT_ROOT}`}/>
                  <PageToDirectPrint selectorId={`#${selectors.PRINT_ROOT}`} isChrome={isChrome()}/>
                </div>
              </div>
              {/* end 時間區間選項 & 列印截圖 */}

              <div css={css `
                  ${isPhone ? flex.v.default : flex.h.mainCenter};
                  width: 100%;
                  margin-top: 16px;
                `}>
                <div css={css `
                    ${sectionItemCss};
                    ${flex.h.allCenter};
                    flex: 2;
                  `}>
                  <div css={accountBoxCss}>
                    <div css={avatarCss}>
                      <Avatar sizes={'large'}>{authState.clientName?.slice(0, 1)}</Avatar>
                    </div>
                    <div css={accountInfoCss}>
                      <p css={accountNameCss}>親愛的{authState.clientName} 先生/女士 您好</p>
                      <div css={accountNumberCss}>
                        <p>帳號：</p>
                        <Select css={css `
                            width: 128px;
                          `} value={authState.selectedIdAccount} onChange={e => authActs.setSelectedIdAccount(e.target.value)}>
                          {authState.clientIdAccounts.map((account, index) => {
                return (<MenuItem key={index} value={account}>
                                <div css={css `
                                    font-size: 16px;
                                  `}>
                                  {account}
                                </div>
                              </MenuItem>);
            })}
                        </Select>
                      </div>
                    </div>
                  </div>
                </div>
                <div css={css `
                    ${sectionItemCss};
                    ${flex.v.crossCenter}
                    ${jc.spaceBetween};
                    flex: 1;
                  `}>
                  <p css={headerItemKeyCss}>期權交易損益(%)：</p>
                  <MyOverallReturnRate />
                </div>
                <div css={css `
                    ${sectionItemCss};
                    ${flex.v.crossCenter};
                    ${jc.spaceBetween};
                    flex: 1;
                  `}>
                  <div>
                    <p css={flex.h.default}>
                      我的表現 &nbsp;
                      <ToolTipDirections />
                    </p>
                  </div>
                  <MyRating />
                </div>
              </div>
              <Line />
              <div css={sectionTitleCss}>帳戶收益分析</div>
              <div css={css `
                  ${isPc ? flex.h.mainCenter : flex.v.allCenter};
                  ${ai.stretch}
                  width: 100%;
                  height: 100%;
                `}>
                <div css={css `
                    ${sectionItemCss};
                    ${flex.v.allCenter}
                  `}>
                  <ReturnRate />
                </div>
                <div css={css `
                    ${sectionItemCss};
                    flex: 1.5;
                    height: 320px;
                    width: ${isPc ? '400px' : 'calc(100% - 16px)'};
                  `}>
                  <AccumulatedReturnRateChart />
                </div>
              </div>
              <Line />
              <div css={sectionTitleCss}>交易數值分析</div>
              <div css={css `
                  width: 100%;
                `}>
                <div css={sectionItemCss}>
                  <TradingStrengthOverall />
                </div>
              </div>
              <Line />
              <div css={sectionTitleCss}>交易紀錄分析：</div>
              <div css={css `
                  ${isPc ? flex.wrap.allCenter : flex.verticalWrap.allCenter};
                  ${ai.stretch};
                  width: 100%;
                `}>
                <div css={css `
                    ${sectionItemCss};
                    ${flex.v.mainCenter};
                    flex: 1;
                  `}>
                  <TradingProporion.Home />
                </div>
                <div css={css `
                    ${sectionItemCss};
                    ${flex.v.mainCenter};
                    flex: 1;
                  `}>
                  <TradingProporion.Foreign />
                </div>
              </div>
              <div css={css `
                  ${isPhone ? flex.v.default : flex.h.mainCenter};
                  width: 100%;
                  height: 100%;
                  margin-top: 16px;
                `}>
                <div css={css `
                    ${flex.v.default};
                    ${sectionItemCss};
                    flex: 1;
                  `}>
                  <TradingFrequency />
                </div>
                <div css={css `
                    ${flex.v.default};
                    ${sectionItemCss};
                    flex: 1;
                  `}>
                  <PositionDuration />
                </div>
              </div>
              <div css={css `
                  ${isPhone ? flex.v.default : flex.h.default};
                  width: 100%;
                `}>
                <div css={css `
                    ${flex.v.default};
                    ${sectionItemCss};
                    flex: 1;
                  `}>
                  <TopThreeProfitAndLoss.Profit />
                </div>
                <div css={css `
                    ${flex.v.default};
                    ${sectionItemCss};
                    flex: 1;
                  `}>
                  <TopThreeProfitAndLoss.Loss />
                </div>
              </div>
            </div>)}
        </div>
        <p css={css `
            color: ${globalRed.r700};
            text-align: center;
            ${fontWeight600};
            ${createMobileCss2(css `
              font-size: 14px;
            `)}
          `}>
          若使用ＩＥ，請改用Chrome或是Edge瀏覽器開啟軟體
        </p>
      </div>
    </useTradingSummaryState.Provider>);
});
const MyRating = memo(function MyRating() {
    const { state } = useTradingSummaryState.useContainer();
    const { overallRanking } = state;
    return (<Rating value={overallRanking / 20} precision={0.5} readOnly size={'large'}/>);
});
const MyOverallReturnRate = memo(function MyOverallReturnRate() {
    const { state } = useTradingSummaryState.useContainer();
    const { groupReturnRates } = state;
    return (<p css={css `
        color: ${!groupReturnRates.mine
            ? globalGrey.g700
            : groupReturnRates.mine > 0
                ? globalRed.r700
                : globalGreen.g700};
        font-size: 1.5rem;
        ${fontWeight600};
      `}>
      {groupReturnRates.mine}
    </p>);
});
const Line = memo(function Line() {
    return (<div className={selectors.PRINT_SHOW} css={css `
        border: 1px solid ${globalGrey.g300};
        width: 100%;
        margin: 24px;
        opacity: 0;
      `}/>);
});
